import { useEffect, useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { ApiError } from "@carepass/api-client";
import { Dialog } from "@carepass/ui";
import { memberSessionStore } from "../../app/runtime";
import { authApi, memberApi } from "../shared/api";
import type { MemberPasswordChangeProof } from "../shared/types";
import { Alert, Button, PageHeader, Surface, navigate } from "../shared/ui";
import { SignupPasswordStep } from "../shared/SignupPinKeypad";
import { clearPasswordChangedSession } from "../auth/password-session";
import { usePasswordChangeExitGuard } from "./usePasswordChangeExitGuard";
import "./PasswordChangePage.css";

type Step = "current" | "new" | "confirmation";
const resetMessage = "비밀번호 확인 시간이 지났거나 변경이 종료되었습니다. 기존 비밀번호부터 다시 확인해 주세요.";

export function PasswordChangePage() {
  const client = useQueryClient();
  const profile = useQuery({ queryKey: ["member", "profile"], queryFn: memberApi.profile, retry: false });
  const [step, setStep] = useState<Step>("current");
  const [current, setCurrent] = useState("");
  const [password, setPassword] = useState("");
  const [confirmation, setConfirmation] = useState("");
  const [proof, setProof] = useState<MemberPasswordChangeProof>();
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string>();
  const requestPending = useRef(false);
  const clearDraft = () => { setCurrent(""); setPassword(""); setConfirmation(""); setProof(undefined); setStep("current"); setPending(false); };
  const exit = usePasswordChangeExitGuard(clearDraft);
  const resetCurrent = () => { exit.reset(); clearDraft(); setError(resetMessage); };

  useEffect(() => {
    if (!proof) return;
    const remaining = Date.parse(proof.expiresAt) - Date.now();
    if (!Number.isFinite(remaining) || remaining <= 0) { resetCurrent(); return; }
    // Completion owns the proof until its response; expiry must not reopen route exit mid-request.
    const timeout = window.setTimeout(() => { if (!requestPending.current) resetCurrent(); }, remaining);
    return () => window.clearTimeout(timeout);
    // The proof is replaced only after server verification; resetting disposes that exact visit.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [proof]);

  const verify = async () => {
    if (requestPending.current || !/^\d{6}$/.test(current)) return;
    requestPending.current = true; setPending(true); setError(undefined);
    const generation = exit.beginVerification();
    try {
      const result = await authApi.verifyPasswordChange({ currentPassword: current });
      if (!exit.acceptProof(result.changeToken, generation)) return;
      setCurrent(""); setProof(result); setStep("new");
    } catch (cause) {
      if (exit.isCurrent(generation)) {
        setCurrent("");
        setError(cause instanceof ApiError ? cause.message : "기존 비밀번호를 확인하지 못했습니다. 다시 시도해 주세요.");
      }
    } finally { requestPending.current = false; if (exit.isCurrent(generation)) setPending(false); }
  };
  const complete = async () => {
    if (requestPending.current || !proof || !/^\d{6}$/.test(confirmation)) return;
    if (password !== confirmation) { setConfirmation(""); setError("비밀번호가 서로 다릅니다. 새 비밀번호를 다시 확인해 주세요."); return; }
    if (Date.parse(proof.expiresAt) <= Date.now()) { resetCurrent(); return; }
    requestPending.current = true; setPending(true); setError(undefined);
    const generation = exit.currentGeneration();
    const sessionAtStart = memberSessionStore.get();
    try {
      await authApi.completePasswordChange({ changeToken: proof.changeToken, password, passwordConfirmation: confirmation });
      const presentResult = exit.isCurrent(generation);
      if (presentResult) { exit.finish(); clearDraft(); }
      const cleared = await clearPasswordChangedSession(client, sessionAtStart);
      if (presentResult && cleared) navigate("/login?passwordChanged=1", true);
    } catch (cause) {
      if (!exit.isCurrent(generation)) return;
      if (Date.parse(proof.expiresAt) <= Date.now() || cause instanceof ApiError && ["MEMBER_PASSWORD_CHANGE_INVALID", "MEMBER_PASSWORD_CHANGE_FORBIDDEN"].includes(cause.code)) resetCurrent();
      else if (cause instanceof ApiError && cause.code === "MEMBER_PASSWORD_MISMATCH") { setConfirmation(""); setError(cause.message); }
      else if (cause instanceof ApiError && cause.code === "MEMBER_PASSWORD_INVALID") { setPassword(""); setConfirmation(""); setStep("new"); setError(cause.message); }
      else setError(cause instanceof ApiError ? cause.message : "비밀번호를 변경하지 못했습니다. 다시 시도해 주세요.");
    } finally { requestPending.current = false; if (exit.isCurrent(generation)) setPending(false); }
  };
  const value = step === "current" ? current : step === "new" ? password : confirmation;
  const change = (next: string) => {
    exit.markDirty(); setError(undefined);
    if (step === "current") setCurrent(next); else if (step === "new") setPassword(next); else setConfirmation(next);
  };
  const findPassword = () => {
    const phone = profile.data?.phoneNumber;
    navigate(phone && /^01\d{8,9}$/.test(phone) ? `/password-reset?phoneNumber=${encodeURIComponent(phone)}` : "/password-reset");
  };
  const busyCompleting = pending && step === "confirmation";
  return <div className="member-password-change">
    <div className="member-signup__progress" aria-label={`3단계 중 ${step === "current" ? 1 : step === "new" ? 2 : 3}단계`}>{[1,2,3].map(index => <span key={index} data-active={index <= (step === "current" ? 1 : step === "new" ? 2 : 3)} />)}</div>
    <PageHeader eyebrow={step === "current" ? "STEP 1 · 기존 비밀번호 확인" : step === "new" ? "STEP 2 · 새 비밀번호 설정" : "STEP 3 · 새 비밀번호 재확인"}
      title={step === "current" ? "기존 비밀번호를 입력해 주세요" : step === "new" ? "새 비밀번호를 설정해 주세요" : "새 비밀번호를 한 번 더 입력해 주세요"}
      description="반복·연속되지 않은 숫자 6자리를 사용해 주세요. 변경 후에는 새 비밀번호로 다시 로그인해야 합니다." />
    {error && <Alert persistent variant="danger" title="확인해 주세요">{error}</Alert>}
    <Surface>
      <SignupPasswordStep key={step} value={value} onChange={change} confirmation={step === "confirmation"} disabled={pending} />
      <Button key={`action-${step}`} size="large" disabled={value.length !== 6 || exit.pending} isLoading={pending} onClick={() => {
        if (step === "current") void verify();
        else if (step === "new") { setError(undefined); setConfirmation(""); setStep("confirmation"); }
        else void complete();
      }}>{step === "current" ? "기존 비밀번호 확인" : step === "new" ? "다음" : "비밀번호 변경"}</Button>
      {step === "current" && <Button variant="quiet" onClick={findPassword}>비밀번호 찾기</Button>}
      <Button variant="quiet" disabled={busyCompleting} onClick={() => navigate("/app/my")}>닫기</Button>
    </Surface>
    <Dialog open={Boolean(exit.destination)} title="비밀번호 변경을 중단할까요?" description="입력한 비밀번호와 확인 정보가 초기화됩니다. 다시 들어오면 기존 비밀번호부터 확인해야 합니다."
      closeDisabled={exit.pending || busyCompleting} onClose={exit.stay}
      secondaryAction={<Button variant="secondary" disabled={exit.pending || busyCompleting} onClick={exit.stay}>계속하기</Button>}
      primaryAction={<Button variant="danger" disabled={busyCompleting} isLoading={exit.pending} onClick={() => void exit.leave()}>나가기</Button>}>
      {busyCompleting && <p>비밀번호 변경 결과를 확인하고 있습니다. 잠시 기다려 주세요.</p>}
      {exit.error && <Alert persistent variant="danger" title="화면을 나가지 못했습니다">{exit.error}</Alert>}
    </Dialog>
  </div>;
}
